<template>
  <div class="mood-card">
    <div class="mood-header">
      <div class="mood-face" v-html="faceIcon" aria-hidden="true"></div>
      <div class="mood-text">
        <h3>{{ moodCopy.title }}</h3>
        <p>{{ moodCopy.sub }}</p>
      </div>
    </div>

    <div class="chart-label">{{ $t('home.mood.chartLabel') }}</div>
    <div class="scatter-wrap">
      <svg viewBox="0 0 320 190" width="100%" preserveAspectRatio="xMidYMid meet">
        <g v-for="v in referenceValues" :key="'line-' + v">
          <line
            :x1="padL"
            :x2="padL + cw"
            :y1="yPos(v)"
            :y2="yPos(v)"
            :stroke="v === 0 ? '#D9D3EF' : '#EDEAF7'"
            stroke-width="1"
          />
          <text
            :x="padL - 8"
            :y="yPos(v) + 3"
            text-anchor="end"
            font-size="8.5"
            fill="#9A90B3"
          >{{ v }}</text>
        </g>

        <path
          :d="trendPath"
          stroke="#D9D3EF"
          stroke-width="1.6"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />

        <g v-for="(point, i) in chartPoints" :key="'point-' + i">
          <g
            v-html="pointFace(point)"
            :transform="'translate(' + (point.x - 10) + ',' + (point.y - 10) + ')'"
          ></g>
          <text
            :x="point.x"
            :y="point.y - 14"
            text-anchor="middle"
            font-size="9"
            font-weight="700"
            fill="#3B1660"
          >{{ point.value }}</text>
          <text
            :x="point.x"
            :y="padT + ch + 16"
            text-anchor="middle"
            font-size="9"
            fill="#9A90B3"
          >{{ point.day }}</text>
        </g>
      </svg>
    </div>

  </div>
</template>

<script>
import { service as HappinessService } from '@/services/hapiness_services.js';

export default {
  name: "moodCardComponent",
  data() {
    return {
      padL: 26,
      padR: 10,
      padT: 14,
      padB: 26,
      maxY: 10,
      moodData: [],
      loading: false
    };
  },
  mounted() {
    this.loadData();
  },
  computed: {
    cw() {
      return 320 - this.padL - this.padR;
    },
    ch() {
      return 190 - this.padT - this.padB;
    },
    referenceValues() {
      return [0, 5, 10];
    },
    bands() {
      return [
        { label: "1 – 3", color: "#F98C86" },
        { label: "3.1 – 6", color: "#F5C46A" },
        { label: "6.1 – 9", color: "#7FD35A" },
      ];
    },
    chartPoints() {
      const count = this.moodData.length || 1;
      return this.moodData.map((d, i) => ({
        day: d.day,
        value: d.value,
        mood: d.mood,
        x: this.xPos(i, count),
        y: this.yPos(d.value),
        color: this.colorOfMood(d.mood) || this.bandOf(d.value).color,
      }));
    },
    trendPath() {
      return this.chartPoints
        .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x},${p.y}`)
        .join(" ");
    },
    average() {
      if (!this.moodData.length) return 0;
      const sum = this.moodData.reduce((s, d) => s + d.value, 0);
      return sum / this.moodData.length;
    },
    averageBand() {
      return this.bandOf(this.average);
    },
    faceIcon() {
      const color = this.averageBand.color;
      const mood = this.averageBand.mood;
      return `<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">${this.faceChildren(color, mood)}</svg>`;
    },
    moodCopy() {
      const mood = this.averageBand.mood;
      return {
        title: this.$t(`home.mood.message.${mood}.title`),
        sub: this.$t(`home.mood.message.${mood}.sub`),
      };
    },
  },
  methods: {
    xPos(i, count) {
      return this.padL + (i / (count - 1)) * this.cw;
    },
    yPos(value) {
      return this.padT + this.ch - (value / this.maxY) * this.ch;
    },
    bandOf(value) {
      if (value <= 3) return { mood: "triste", color: "#F98C86" };
      if (value <= 6) return { mood: "neutro", color: "#F5C46A" };
      return { mood: "feliz", color: "#7FD35A" };
    },
    colorOfMood(mood) {
      const colors = {
        triste: "#F98C86",
        sad: "#F98C86",
        neutro: "#F5C46A",
        neutral: "#F5C46A",
        feliz: "#7FD35A",
        happy: "#7FD35A",
      };
      return colors[mood];
    },
    faceChildren(color, mood) {
      const mouths = {
        triste: "M6 14c1.5-3 7.5-3 9 0",
        neutro: "M6 13h9",
        feliz: "M6 11c1.5 3 7.5 3 9 0",
      };
      return `<circle cx="10" cy="10" r="9" fill="${color}"/>` +
        `<circle cx="7" cy="7" r="1.5" fill="#3B1660"/>` +
        `<circle cx="13" cy="7" r="1.5" fill="#3B1660"/>` +
        `<path d="${mouths[mood]}" stroke="#3B1660" stroke-width="1.3" fill="none" stroke-linecap="round"/>`;
    },
    pointFace(point) {
      return this.faceChildren(point.color, point.mood);
    },
    mapMood(mood) {
      const map = {
        sad: "triste",
        neutral: "neutro",
        happy: "feliz",
      };
      return map[mood] || "neutro";
    },
    formatDay(dateStr) {
      const d = new Date(dateStr + "T00:00:00");
      return this.$t('home.mood.day.' + d.getDay()) || dateStr;
    },
    async loadData() {
      const idGroup = sessionStorage.getItem("idGroup");
      const idEmpresa = sessionStorage.getItem("idEmpresa");
      const idUsuario = sessionStorage.getItem("idUsuario");
      if (!idGroup) return;
      this.loading = true;
      try {
        const resp = await HappinessService.getWeeklyAverage(idGroup, idEmpresa, idUsuario);
        if (resp.data && resp.data.payload) {
          const list = [...resp.data.payload]
            .sort((a, b) => new Date(a.date + "T00:00:00") - new Date(b.date + "T00:00:00"))
            .slice(-7);
          this.moodData = list.map((item) => ({
            day: this.formatDay(item.date),
            value: item.score,
            mood: this.mapMood(item.mood),
          }));
        }
      } catch (err) {
        console.error("Error cargando weeklyAverage", err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.mood-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 26px 28px;
  box-shadow: 0 10px 30px -14px rgba(59, 22, 96, 0.16);
  display: flex;
  flex-direction: column;
}

.mood-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.mood-face {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

.mood-face >>> svg {
  width: 100%;
  height: 100%;
  display: block;
}

.mood-text h3 {
  font-family: "Amsi Pro", "Poppins", sans-serif;
  font-size: 15.5px;
  font-weight: 600;
  color: #3b1660;
  line-height: 1.35;
  margin: 0 0 3px;
}

.mood-text p {
  font-family: "Inter", "Nunito Sans", sans-serif;
  font-size: 12.5px;
  color: #6e6480;
  line-height: 1.45;
  margin: 0;
}

.chart-label {
  font-size: 12px;
  color: #6e6480;
  font-weight: 600;
  margin-bottom: 10px;
}

.scatter-wrap {
  width: 100%;
}

.hist-legend {
  display: flex;
  gap: 14px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: #6e6480;
}

.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}
</style>
